import { Link, useRouterState, useNavigate } from '@tanstack/react-router'
import { BarChart3, FolderOpen, Sparkles, Users, LogOut, PanelLeftClose, PanelLeft, MessageSquare, SquareKanban } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { cn, isQolaigEmail } from '@/lib/utils'

const SIDEBAR_WIDTH = 240
const SIDEBAR_COLLAPSED_WIDTH = 72

type Role = 'admin' | 'client' | 'super_admin'

const navItems: Array<{
  to: string
  label: string
  icon: typeof BarChart3
  roles: Role[]
  /** Si true : affiché seulement pour les adresses @qolaig.com */
  qolaigEmailOnly?: boolean
}> = [
  { to: '/projects/dashboard', label: 'Analytics', icon: BarChart3, roles: ['super_admin'] },
  { to: '/projects', label: 'Projets', icon: FolderOpen, roles: ['admin', 'client', 'super_admin'] },
  { to: '/tickets', label: 'Tickets', icon: SquareKanban, roles: ['admin', 'super_admin'] },
  { to: '/workshops', label: 'Workshops', icon: Sparkles, roles: ['admin', 'super_admin'] },
  {
    to: '/standup',
    label: 'Standup',
    icon: MessageSquare,
    roles: ['admin', 'super_admin'],
    qolaigEmailOnly: true,
  },
  { to: '/projects/users-tracking', label: 'Utilisateurs', icon: Users, roles: ['admin', 'super_admin'] },
]

function isAnalytics(path: string) {
  return path === '/projects/dashboard'
}

function isUsers(path: string) {
  return path === '/projects/users-tracking'
}

function isProjets(path: string) {
  return path === '/projects' || /^\/projects\/\d+$/.test(path)
}

function isTickets(path: string) {
  return path === '/tickets'
}

function isWorkshops(path: string) {
  return path === '/workshops'
}

function isStandup(path: string) {
  return path === '/standup'
}

function isNavActive(to: string, path: string): boolean {
  if (to === '/projects/dashboard') return isAnalytics(path)
  if (to === '/projects/users-tracking') return isUsers(path)
  if (to === '/projects') return isProjets(path)
  if (to === '/tickets') return isTickets(path)
  if (to === '/workshops') return isWorkshops(path)
  if (to === '/standup') return isStandup(path)
  return path.startsWith(to)
}

function getRoleBadgeLabel(role: string): string {
  switch (role) {
    case 'super_admin':
      return 'Administrateur'
    case 'admin':
      return 'Administrateur'
    case 'client':
      return 'Client'
    default:
      return role
  }
}

function canSeeNavItem(roles: Role[], userRole: Role | undefined): boolean {
  if (!userRole) return false
  return roles.includes(userRole)
}

export function Sidebar({
  isOpen,
  onToggle,
}: {
  isOpen: boolean
  onToggle: () => void
}) {
  const router = useRouterState()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const path = router.location.pathname
  const userRole = user?.role as Role | undefined
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'

  const handleLogout = async () => {
    try {
      await logout()
      navigate({ to: '/' })
    } catch (e) {
      console.error('Logout:', e)
      navigate({ to: '/' })
    }
  }

  const visibleNavItems = navItems.filter((item) => {
    if (!canSeeNavItem(item.roles, userRole)) return false
    if (item.qolaigEmailOnly && !isQolaigEmail(user?.email)) return false
    return true
  })

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 flex h-screen flex-col bg-[#f5f6f8] border-r border-gray-200/80 transition-[width] duration-200 ease-in-out',
        isOpen ? 'w-[240px]' : 'w-[72px]'
      )}
    >
      {/* Logo (image uniquement) + Toggle */}
      <div
        className={cn(
          'flex shrink-0 border-b border-gray-200/80 px-3 transition-all',
          isOpen
            ? 'flex-row items-center justify-between pt-6 pb-4 gap-2'
            : 'flex-col items-center pt-4 pb-3 gap-3'
        )}
      >
        <Link
          to="/projects"
          className={cn(
            'flex items-center justify-center overflow-hidden',
            isOpen ? 'min-w-0 flex-1' : 'w-10 h-10'
          )}
        >
          <img
            src="/assets/images/logo.png"
            alt="Qolaig"
            className={cn('object-contain', isOpen ? 'h-10 w-auto' : 'h-8 w-8')}
          />
        </Link>
        <button
          type="button"
          onClick={onToggle}
          className="flex shrink-0 items-center justify-center rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          title={isOpen ? 'Réduire le menu' : 'Ouvrir le menu'}
        >
          {isOpen ? (
            <PanelLeftClose className="h-5 w-5" />
          ) : (
            <PanelLeft className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav
        className={cn(
          'flex-1 space-y-0.5 overflow-y-auto py-3',
          isOpen ? 'px-3' : 'px-2'
        )}
      >
        {visibleNavItems.map(({ to, label, icon: Icon }) => {
          const active = isNavActive(to, path)
          return (
            <Link
              key={to}
              to={to}
              title={!isOpen ? label : undefined}
              className={cn(
                'flex items-center rounded-lg text-sm font-medium transition-colors',
                isOpen ? 'gap-3 px-3 py-2.5' : 'justify-center p-2.5',
                active
                  ? 'bg-[#e0f7fa] text-[#0d9488]'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              )}
            >
              <Icon
                className={cn('h-5 w-5 shrink-0', active ? 'text-[#22b8cf]' : 'text-gray-500')}
              />
              {isOpen && <span>{label}</span>}
            </Link>
          )
        })}
      </nav>

      {/* User section */}
      <div
        className={cn(
          'shrink-0 border-t border-gray-200/80 space-y-3 transition-all',
          isOpen ? 'p-4' : 'p-2'
        )}
      >
        {isOpen && (
          <>
            <p className="text-xs text-gray-500">Connecté en tant que</p>
            <p className="text-sm font-semibold text-gray-800 truncate" title={user?.email}>
              {user?.email ?? '—'}
            </p>
            {user && (
              <span
                className={cn(
                  'inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-medium',
                  isAdmin
                    ? 'border-[#22b8cf]/40 bg-[#e0f7fa] text-[#0d9488]'
                    : 'border-gray-300 bg-gray-100 text-gray-700'
                )}
              >
                {getRoleBadgeLabel(user.role)}
              </span>
            )}
          </>
        )}
        <button
          type="button"
          onClick={handleLogout}
          title="Déconnexion"
          className={cn(
            'flex w-full items-center rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors',
            isOpen ? 'gap-3 px-3 py-2.5' : 'justify-center p-2.5'
          )}
        >
          <LogOut className="h-5 w-5 shrink-0 text-gray-500" />
          {isOpen && <span>Déconnexion</span>}
        </button>
      </div>
    </aside>
  )
}

export { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH }
